import type { HDStethEcgSnapshot } from './ecg-types'
import { HDSTETH_SAMPLE_RATE_HZ, HDStethBatch } from './types'

export const ECG_STALE_MS = 2000
export const ECG_TOPIC = 'hdsteth-ecg'
/** Batches without ECG shorter than this keep the trace instead of flashing "waiting". */
export const ECG_GAP_TOLERANCE_MS = 500
const DISPLAY_WINDOW_SECONDS = 5
const MAX_PACKET_SAMPLES = 128

export interface HDStethEcgMessage {
  type: typeof ECG_TOPIC
  version: 1
  streamId: string
  sequence: number
  state: 'samples' | 'stopped'
  mode: HDStethBatch['mode']
  sampleRateHz: number
  samples: number[]
  simulated: boolean
}

export interface HDStethEcgTransport {
  send: (message: HDStethEcgMessage) => void
  subscribe: (listener: (message: unknown) => void) => () => void
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Validate bounded ECG packets before they enter a buffer or a canvas. */
export function isEcgMessage(value: unknown): value is HDStethEcgMessage {
  if (!isRecord(value) || value.type !== ECG_TOPIC || value.version !== 1) return false
  if (typeof value.streamId !== 'string' || value.streamId.length < 1 || value.streamId.length > 64) return false
  if (typeof value.sequence !== 'number' || !Number.isSafeInteger(value.sequence) || value.sequence < 0) return false
  if (value.state !== 'samples' && value.state !== 'stopped') return false
  if (value.mode !== '6-bit' && value.mode !== '12-bit') return false
  if (typeof value.sampleRateHz !== 'number' || !Number.isFinite(value.sampleRateHz) || value.sampleRateHz < 50 || value.sampleRateHz > 2000) return false
  if (typeof value.simulated !== 'boolean' || !Array.isArray(value.samples) || value.samples.length > MAX_PACKET_SAMPLES) return false
  const max = value.mode === '12-bit' ? 4095 : 63
  return (value.state !== 'stopped' || value.samples.length === 0)
    && value.samples.every((sample: unknown) => typeof sample === 'number' && Number.isInteger(sample) && sample >= 0 && sample <= max)
}

/** Packetize one selected HD Steth session; every packet fits below Chime's 2 KB limit. */
export class HDStethEcgSender {
  private readonly streamId: string = crypto.randomUUID()
  private sequence: number = 0

  packets(batch: HDStethBatch): HDStethEcgMessage[] {
    const result: HDStethEcgMessage[] = []
    for (let offset = 0; offset < Math.max(1, batch.ecg.length); offset += MAX_PACKET_SAMPLES) {
      result.push({ ...this.header(), state: 'samples', mode: batch.mode, sampleRateHz: batch.ecgSampleRateHz ?? HDSTETH_SAMPLE_RATE_HZ,
        samples: batch.ecg.slice(offset, offset + MAX_PACKET_SAMPLES), simulated: batch.simulated === true })
    }
    return result
  }

  stop(): HDStethEcgMessage {
    return { ...this.header(), state: 'stopped', mode: '12-bit', sampleRateHz: HDSTETH_SAMPLE_RATE_HZ, samples: [], simulated: false }
  }

  private header(): Pick<HDStethEcgMessage, 'type' | 'version' | 'streamId' | 'sequence'> {
    return { type: ECG_TOPIC, version: 1, streamId: this.streamId, sequence: this.sequence++ }
  }
}

/** Five-second display buffer. Lost packets clear the trace instead of joining unrelated samples. */
export class HDStethEcgReceiver {
  private streamId: string | null = null
  private sequence: number = -1
  private retired: Set<string> = new Set<string>()
  private lastReceivedAt: number = 0
  private lastSampleAt: number = 0
  private snapshot: HDStethEcgSnapshot | undefined

  receive(value: unknown, now: number): HDStethEcgSnapshot | undefined {
    if (!isEcgMessage(value) || this.retired.has(value.streamId)) return this.current(now)
    if (value.streamId !== this.streamId) this.switchStream(value.streamId)
    if (value.sequence <= this.sequence) return this.current(now)
    const continuous = value.sequence === this.sequence + 1 && now - this.lastReceivedAt <= ECG_STALE_MS
      && this.snapshot?.sampleRateHz === value.sampleRateHz && this.snapshot.mode === value.mode
    this.sequence = value.sequence
    this.lastReceivedAt = now
    if (value.state === 'stopped') { this.retired.add(value.streamId); return this.clear() }
    if (!value.samples.length && continuous && this.isWithinGapTolerance(now)) return this.snapshot
    if (value.samples.length) this.lastSampleAt = now
    const previous = continuous && value.samples.length ? this.snapshot?.samples ?? [] : []
    this.snapshot = { mode: value.mode, sampleRateHz: value.sampleRateHz,
      simulated: value.simulated, status: value.samples.length ? 'live' : 'waiting',
      samples: [...previous, ...value.samples].slice(-Math.round(value.sampleRateHz * DISPLAY_WINDOW_SECONDS)) }
    return this.snapshot
  }

  current(now: number): HDStethEcgSnapshot | undefined {
    if (now - this.lastReceivedAt > ECG_STALE_MS) return this.clear()
    return this.snapshot
  }

  clear(): undefined {
    this.snapshot = undefined
    return undefined
  }

  private isWithinGapTolerance(now: number): boolean {
    return this.snapshot?.status === 'live' && now - this.lastSampleAt <= ECG_GAP_TOLERANCE_MS
  }

  private switchStream(streamId: string): void {
    if (this.streamId) this.retired.add(this.streamId)
    if (this.retired.size > 32) this.retired.delete(this.retired.values().next().value as string)
    this.streamId = streamId
    this.sequence = -1
    this.clear()
  }
}
